(() => {
  if (window.__voicskySlideshowScrollCardReady) return;
  window.__voicskySlideshowScrollCardReady = true;

  const sections = new Set();
  const metrics = new WeakMap();
  const pageWrapper = document.querySelector('.page-wrapper');
  let scrollContainer = null;
  let frame = 0;
  let resizeFrame = 0;
  const DEFAULT_CARD_ENTER_PROGRESS = 0.85;
  const CARD_HYSTERESIS = 0.1;
  const MIN_CARD_HEIGHT = 320;

  const getScrollPosition = () => scrollContainer === window
    ? (window.scrollY || document.documentElement.scrollTop || 0)
    : scrollContainer?.scrollTop || 0;

  const bindScrollContainer = () => {
    const nextContainer = window.matchMedia('(min-width: 990px)').matches && pageWrapper ? pageWrapper : window;
    if (nextContainer === scrollContainer) return;
    scrollContainer?.removeEventListener('scroll', scheduleUpdate);
    scrollContainer = nextContainer;
    scrollContainer.addEventListener('scroll', scheduleUpdate, { passive: true });
  };

  const measure = (section) => {
    if (!section.isConnected || !window.matchMedia('(min-width: 750px)').matches) return;

    const scrollPosition = getScrollPosition();
    const styles = getComputedStyle(section);
    const thumbs = section.querySelector('.voicsky-liquid-slideshow__thumbs');
    const headerColumns = document.querySelector('#header-component .header__row--top .header__columns');
    const configuredTopInset = parseFloat(styles.getPropertyValue('--vls-padding-top')) || 0;
    const sectionRect = section.getBoundingClientRect();
    const headerColumnsRect = headerColumns?.getBoundingClientRect();
    const previous = metrics.get(section);
    // The card starts below the visible header columns, relative to the
    // slideshow itself. Cache this while the section is at its natural top;
    // once scrolled, viewport coordinates would otherwise inflate the inset.
    const measuredHeaderClearance = headerColumnsRect
      ? Math.max(0, headerColumnsRect.bottom - sectionRect.top)
      : 0;
    const headerClearance = sectionRect.top >= -1 || previous?.headerClearance == null
      ? measuredHeaderClearance
      : previous.headerClearance;
    const bottomPadding = parseFloat(styles.getPropertyValue('--vls-padding-bottom')) || 0;
    const gap = parseFloat(styles.getPropertyValue('--vls-gap')) || 0;
    const thumbHeight = thumbs?.offsetHeight || 150;
    const headerGap = Math.max(0, parseFloat(styles.getPropertyValue('--vls-header-gap')) || 0);
    const requestedTopInset = Math.max(configuredTopInset, headerClearance + headerGap);
    const requestedBottomInset = bottomPadding + thumbHeight + gap;
    const sectionHeight = section.clientHeight || section.offsetHeight || window.innerHeight;
    const maximumCombinedInset = Math.max(0, sectionHeight - MIN_CARD_HEIGHT);
    const topInset = Math.min(requestedTopInset, maximumCombinedInset);
    const bottomInset = Math.min(
      requestedBottomInset,
      Math.max(0, maximumCombinedInset - topInset)
    );
    const radius = parseFloat(styles.getPropertyValue('--vls-scroll-card-radius')) || 28;
    const borderOpacity = parseFloat(styles.getPropertyValue('--vls-scroll-card-border-opacity')) || 0.55;
    const configuredTrigger = parseFloat(styles.getPropertyValue('--vls-scroll-card-trigger'));
    const cardEnterProgress = Number.isFinite(configuredTrigger)
      ? Math.min(0.95, Math.max(0.5, configuredTrigger))
      : DEFAULT_CARD_ENTER_PROGRESS;
    metrics.set(section, {
      documentTop: sectionRect.top + scrollPosition,
      completeAt: Math.max(1, Math.min(section.offsetHeight * 0.5, window.innerHeight * 0.5)),
      lastProgress: previous?.lastProgress ?? -1,
      cardActive: previous?.cardActive ?? false,
      headerClearance,
      cardEnterProgress,
      cardExitProgress: Math.max(0, cardEnterProgress - CARD_HYSTERESIS),
    });

    section.style.setProperty('--vls-scroll-target-top', `${topInset}px`);
    section.style.setProperty('--vls-scroll-target-bottom', `${bottomInset}px`);
    section.style.setProperty('--vls-scroll-target-radius', `${radius}px`);
    section.style.setProperty('--vls-scroll-target-border-alpha', String(borderOpacity));
    section.style.setProperty('--vls-scroll-target-highlight-alpha', String(borderOpacity * 0.64));
    section.style.setProperty('--vls-scroll-target-shadow-alpha', '0.16');
    section.style.setProperty('--vls-scroll-target-controls-bottom-start', `${bottomInset + 20}px`);
  };

  const measureAll = () => sections.forEach((section) => measure(section));

  const register = (root = document) => {
    if (root.matches?.("voicsky-liquid-slideshow[data-scroll-card='true']")) sections.add(root);
    root.querySelectorAll?.("voicsky-liquid-slideshow[data-scroll-card='true']").forEach((section) => sections.add(section));
    requestAnimationFrame(() => {
      measureAll();
      update();
      requestAnimationFrame(() => {
        sections.forEach((section) => {
          if (section.isConnected) section.classList.add('is-scroll-card-ready');
        });
      });
    });
  };

  const update = () => {
    frame = 0;
    const desktop = window.matchMedia('(min-width: 750px)').matches;
    const scrollPosition = getScrollPosition();

    sections.forEach((section) => {
      if (!section.isConnected) {
        sections.delete(section);
        return;
      }

      if (!desktop) {
        if (section.style.getPropertyValue('--vls-scroll-progress') !== '0') {
          section.style.setProperty('--vls-scroll-progress', '0');
        }
        return;
      }

      let cached = metrics.get(section);
      if (!cached) {
        measure(section);
        cached = metrics.get(section);
      }
      if (!cached) return;

      const travelled = Math.max(0, scrollPosition - Math.max(0, cached.documentTop));
      const scrollProgress = Math.min(1, travelled / cached.completeAt);
      const forcedCard = section.dataset.displayMode === 'card';

      if (forcedCard) {
        cached.cardActive = true;
      } else if (cached.cardActive) {
        cached.cardActive = scrollProgress > cached.cardExitProgress;
      } else {
        cached.cardActive = scrollProgress >= cached.cardEnterProgress;
      }

      const progress = cached.cardActive ? 1 : 0;
      if (progress === cached.lastProgress) return;
      cached.lastProgress = progress;
      section.dataset.scrollCardState = cached.cardActive ? 'card' : 'fullscreen';
      section.style.setProperty('--vls-scroll-progress', String(progress));
      document.dispatchEvent(new CustomEvent('voicsky:slideshow-card-state', {
        detail: { section, cardMode: cached.cardActive },
      }));
    });
  };

  const scheduleUpdate = () => {
    if (frame) return;
    frame = requestAnimationFrame(update);
  };

  const handleResize = () => {
    if (resizeFrame) return;
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = 0;
      bindScrollContainer();
      measureAll();
      update();
    });
  };

  bindScrollContainer();
  window.addEventListener('resize', handleResize, { passive: true });
  window.visualViewport?.addEventListener('resize', handleResize, { passive: true });
  document.addEventListener('voicsky:slideshow-display-mode', scheduleUpdate);
  document.addEventListener('shopify:section:load', (event) => register(event.target));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => register());
  else register();
})();
